import React, { useMemo, useCallback } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { Phone as PhoneIcon, PhoneDisabled as PhoneDisabledIcon } from '@mui/icons-material';
import DraggableItemsPanel from '../../../shared/components/DraggableItemsPanel';
import { rankToDisplayNumber } from '../../../shared/utils/ranking';
import type { ContactWithUser } from '../../../types';

interface OperationalCallChainPanelProps {
  contacts: ContactWithUser[];
  onReorder: (orderedUsernames: string[]) => void;
}

interface RankedContact {
  username: string;
  name: string;
  email: string;
  phone_number?: string;
  rank: number;
}

const OperationalCallChainPanel: React.FC<OperationalCallChainPanelProps> = ({ contacts, onReorder }) => {
  const rankedContacts: RankedContact[] = useMemo(
    () =>
      contacts.map((c) => ({
        username: c.username,
        name: c.name,
        email: c.email,
        phone_number: c.phone_number,
        rank: c.operational_priority ?? 0,
      })),
    [contacts],
  );

  const handleReorder = useCallback(
    (reordered: RankedContact[]) => {
      onReorder(reordered.map((c) => c.username));
    },
    [onReorder],
  );

  const renderItem = useCallback((item: RankedContact, index: number) => (
    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
      <Chip
        label={`#${rankToDisplayNumber(index)}`}
        size="small"
        color="primary"
        sx={{ fontWeight: 'bold', minWidth: 36 }}
      />
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" fontWeight="medium">
          {item.name || item.email}
        </Typography>
        {item.phone_number && (
          <Typography variant="caption" color="text.secondary">
            {item.phone_number}
          </Typography>
        )}
      </Box>
    </Box>
  ), []);

  const getItemId = useCallback((item: RankedContact) => item.username, []);

  return (
    <DraggableItemsPanel
      title="Call Chain"
      titleIcon={<PhoneIcon fontSize="small" />}
      subtitle="Drag to reorder the operational escalation sequence"
      items={rankedContacts}
      emptyStateIcon={<PhoneDisabledIcon sx={{ fontSize: 40, mb: 1 }} />}
      emptyStateText="No operational contacts"
      emptyStateSubtext="Assign the 'operational' type to contacts to build a call chain"
      onReorderItems={handleReorder}
      renderItem={renderItem}
      getItemId={getItemId}
      droppableId="operational-call-chain"
    />
  );
};

export default OperationalCallChainPanel;
